<template>
  <div :class="editorClasses">
    <template v-if="!mismatchError">
      <nx-content-editor-header
        v-if="!loading"
        :modal="modal"
      >
        <template #info>
          <nx-content-editor-header-info
            :is-published="isPublished"
            :is-scheduled="hasScheduledPublication"
            :schedule-date="scheduleDate"
            :url="publicUrl"
            :title="title"
            :public-url="publicUrl"
          />
        </template>

        <template #chips>
          <component
            :is="siteChipsComponent"
            v-if="siteChipsComponent"
            :chips="siteChipsItems"
          />
          <component
            :is="chipsComponent"
            :id="id"
            :type="type"
            :creation-date="creationDate"
            :publication-date="publicationDate"
            :is-draft="isDraft"
            :is-scheduled="hasScheduledPublication"
            :is-published="chipsComponentIsPublished"
            :has-redirection="hasRedirection"
            :schedule-unpublish-date="scheduleUnpublishDate"
            :schedule-publish-date="scheduleDate"
            :unpublished-date="unpublishedDate"
            :relative-version-id="relativeVersionId"
            :entity-published-version-start-publication-date="entityPublishedVersionStartPublicationDate"
            :has-been-published="hasBeenPublished"
          >
            <nx-content-editor-header-specific-chips
              :value="metadata"
              :type="type"
            />
          </component>
        </template>

        <template #tools>
          <nx-entity-metadata-button
            :id="id"
            :type="type"
            color="white"
            tooltip="bottom"
            class="ml-2"
          />
          <nx-content-editor-tools
            :editor-store-path="storePath"
            @reload:editor="init"
            @updateRelativeVersionId="updateRelativeVersionId"
          />
        </template>

        <template #extension>
          <nx-content-editor-language-selector
            v-if="supportsMultilang && !modal"
            :editor-store-path="storePath"
            :current="currentLanguage"
            :available="availableLanguages"
            @modifyAvailableLanguages="modifyAvailableLanguages"
          />
          <nx-content-editor-channel-tabs
            v-if="!modal"
            :editor-store-path="storePath"
            @add="addChannel"
            @edit="editChannel"
            @remove="removeChannel"
          />
          <v-spacer />

          <nx-content-editor-header-buttons
            v-if="!loading && type && channel"
            :disabled="disabled"
            :channel="channel"
            :type="type"
            :component="channelHeaderButtonsComponent"
            @save="onSave"
            @validate="validateForm"
            @validate:publish="validateFormForPublish"
            @updateRelativeVersionId="updateRelativeVersionId"
          />
        </template>
      </nx-content-editor-header>
      <v-container
        v-if="!loading && type && channel"
        id="nx-content-editor-container"
        class="nx-container white"
        fluid
      >
        <v-layout justify-center>
          <v-flex
            v-show="isPreview"
            key="preview"
            :class="isPreviewOnMobile ? 'xs12' : 'xs6'"
          >
            <nx-content-editor-preview
              ref="preview"
              :show="isPreview"
              :editor-store-path="storePath"
            />
          </v-flex>
          <v-flex
            v-show="!isPreviewOnMobile"
            key="form"
            :class="isPreview ? 'xs6' : 'xs12'"
          >
            <nx-entity-form-generator
              :id="id"
              :key="channel + disabled"
              ref="form"
              :disabled="disabled || hasBeenPublished"
              :type="type"
              :channel="currentChannel"
              :editor-fields-config="editorFieldsConfig"
              :server-blocked-message="channelServerBlock"
              :draft="draft"
              @update:field="updateField"
              @update:form-flags="updateFormFlags"
              @update:form-errors="updateFormErrors"
              @update:form-fields-flags="updateFormFieldsFlags"
            />
          </v-flex>
        </v-layout>
      </v-container>
    </template>
    <nx-site-mismatch-error
      v-else
      v-bind="mismatchError"
      target="_blank"
    />
    <nx-dialog-confirm
      ref="confirm"
      :text="$t('message.content.editor.deleteChannelConfirmText')"
      :title="$t('message.content.editor.deleteChannelConfirmTitle')"
      accept-color="error"
    />
  </div>
</template>

<script>
import uuid from 'uuid/v4';
import NxContentEditorPreview from '@/modules/content/editor/components/Preview';
import Storable from '@/mixins/storable';
import Loadable from '@/mixins/loadable';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import NxEntityFormGenerator from '@/modules/entity/editor/EntityFormGenerator';
import NxContentEditorHeaderButtons from '@/modules/content/editor/components/buttons/HeaderButtons';
import NxContentEditorChannelTabs from '@/modules/content/editor/components/ChannelTabs';
import NxContentEditorLanguageSelector from '@/modules/content/editor/LanguageSelector';
import NxContentEditorTools from '@/modules/content/editor/Tools';
import NxContentEditorHeaderSpecificChips from '@/modules/content/editor/components/chips/HeaderSpecificChips';
import NxContentEditorHeaderCommonChips from '@/modules/content/editor/components/chips/HeaderCommonChips';
import NxChip from '@/components/chips/Chip';
import NxContentEditorVersionableCommonChips
  from '@/modules/content/editor/components/version/ContentVersionHeaderCommonChips';
import NxContentEditorHeaderInfo from '@/modules/content/editor/components/HeaderInfo';
import NxEntityMetadataButton from '@/modules/entity/metadata/EntityMetadataButton';
import { isEmpty } from 'lodash';
import { SiteMismatchError } from '@/api/interceptors/format-error';
import NxSiteMismatchError from '@/components/errors/LayoutSiteMismatchError';
import { siteChips } from '@/modules/content/editor/config';
import store from './store';
import NxContentEditorHeader from './components/Header';

export default {
  name: 'NxContentEditor',
  components: {
    NxEntityMetadataButton,
    NxContentEditorHeaderInfo,
    NxContentEditorHeaderCommonChips,
    NxContentEditorHeaderSpecificChips,
    NxContentEditorTools,
    NxContentEditorLanguageSelector,
    NxContentEditorChannelTabs,
    NxContentEditorHeaderButtons,
    NxEntityFormGenerator,
    NxDialogConfirm,
    NxContentEditorPreview,
    NxContentEditorHeader,
    NxChip,
    NxSiteMismatchError,
  },
  mixins: [Storable, Loadable],
  metaInfo() {
    if (this.modal) {
      return undefined;
    }
    return {
      title: this.title,
      // all titles will be injected into this template
      titleTemplate: this.title ? '%s | BCube CMS' : 'BCube CMS',
    };
  },
  provide() {
    return {
      reloadPreview: this.reloadPreview,
      $getParentEditor: () => this,
    };
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    relativeVersionId: {
      type: Number,
    },
    type: {
      type: String,
      required: true,
    },
    channel: {
      type: String,
    },
    language: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    modal: {
      type: Boolean,
    },
  },
  data() {
    return {
      mismatchError: null,
    };
  },
  computed: {
    siteChipsComponent() {
      if (siteChips === undefined
        || isEmpty(siteChips.component)) {
        return undefined;
      }
      return siteChips.component;
    },
    siteChipsItems() {
      if (!this.metadata
        || !this.metadata.list
      ) {
        return [];
      }
      return this.metadata.list.chips;
    },
    storePath() {
      return ['content', 'editor', uuid()];
    },
    isPreview() {
      return this.state.isPreview;
    },
    currentLanguage() {
      return this.state.currentLanguage;
    },
    currentChannel() {
      return this.state.currentChannel;
    },
    availableLanguages() {
      return this.state.availableLanguages;
    },
    channelHeaderButtonsComponent() {
      return this.getters.channelHeaderButtonsComponent;
    },
    creationDate() {
      return this.getters.creationDate;
    },
    hasRedirection() {
      return this.getters.hasRedirection;
    },
    hasScheduledPublication() {
      return this.getters.hasScheduledPublication || this.state.entityScheduled;
    },
    isDraft() {
      return this.getters.isDraft;
    },
    isPublished() {
      return this.getters.isPublished;
    },
    chipsComponentIsPublished() {
      if (this.versionable) {
        return this.getters.versionIsPublished;
      }
      return this.isPublished;
    },
    draft() {
      return this.getters.draft;
    },
    editorFieldsConfig() {
      return this.getters.editorFieldsConfig;
    },
    metadata() {
      return this.getters.metadata;
    },
    publicationDate() {
      return this.getters.publicationDate;
    },
    publicUrl() {
      return this.getters.publicUrl;
    },
    channelServerBlock() {
      return this.getters.channelServerBlock;
    },
    scheduleDate() {
      return this.getters.scheduleDate;
    },
    scheduleUnpublishDate() {
      return this.getters.scheduleUnpublishDate;
    },
    title() {
      return this.getters.title;
    },
    unpublishedDate() {
      return this.getters.unpublishedDate;
    },
    entityPublishedVersionStartPublicationDate() {
      return this.getters.entityPublishedVersionStartPublicationDate;
    },
    hasBeenPublished() {
      return this.getters.hasBeenPublished;
    },
    editorClasses() {
      return {
        'nx-content-editor': true,
        'is-preview': this.isPreview,
        modal: this.modal,
      };
    },
    supportsMultilang() {
      return this.currentLanguage && this.availableLanguages.length > 1;
    },
    versionable() {
      return this.getters.versionable;
    },
    chipsComponent() {
      if (this.versionable) {
        return NxContentEditorVersionableCommonChips;
      }
      return NxContentEditorHeaderCommonChips;
    },
    isPreviewOnMobile() {
      return this.isPreview && this.$vuetify.breakpoint.xs;
    },
  },
  watch: {
    id() {
      this.init();
    },
    relativeVersionId(newValue, oldValue) {
      if (!oldValue) {
        // Initialized relativeVersionId
        return;
      }
      this.init();
    },
    language() {
      this.init();
    },
    disabled(newValue, oldValue) {
      if (oldValue === true && newValue === false) {
        // Refresh the editor on enabled
        this.init();
      }
    },
  },
  created() {
    this.loading = true;
    this.$store.registerModule(this.storePath, store);
    window.addEventListener('beforeunload', (event) => {
      if (this.state.isDirty) {
        event.returnValue = '';
      }
    });
  },
  beforeDestroy() {
    this.$store.unregisterModule(this.storePath);
  },
  mounted() {
    this.init();
  },
  methods: {
    addChannel(channel) {
      this.dispatch('ADD_CHANNEL', channel);
    },
    async removeChannel(channel) {
      const agree = await this.$refs.confirm.open();
      if (agree) {
        this.dispatch('REMOVE_CHANNEL', channel);
      }
    },
    async init() {
      this.loadingStart();
      try {
        const { relativeVersionId } = await this.dispatch('INIT_EDITOR', {
          id: this.id,
          relativeVersionId: this.relativeVersionId,
          type: this.type,
          channel: this.channel,
          language: this.language,
        });
        if (!this.relativeVersionId && relativeVersionId) {
          this.updateRelativeVersionId(relativeVersionId);
        }
      } catch (err) {
        console.error(err);
        if (err instanceof SiteMismatchError) {
          this.handlerSiteMismatchError(err);
          return;
        }
        if (err.response.status === 403) {
          this.$router.push({
            name: 'unauthorized',
            path: this.$route.path,
          });
        } else {
          this.$router.push({
            name: 'notFound',
            path: this.$route.path,
          });
        }
      }

      this.$emit('load');
      await this.$store.dispatch('activity/PUSH_ACTIVITY', {
        entity: this.type,
        id: this.id,
        title: this.title,
      });
      this.loadingFinish();
    },
    handlerSiteMismatchError(err) {
      if (this.modal) {
        this.mismatchError = err.cmsVariables;
        const { href } = this.$router.resolve({
          name: 'content.editor',
          params: {
            id: this.id,
            type: this.type,
            site: err.cmsVariables.requestedSiteAlias,
          },
        });
        this.mismatchError.path = href;
      } else {
        this.$router.push({
          name: 'siteMismatch',
          params: {
            ...err.cmsVariables,
            path: this.$route.path,
          },
        });
      }
    },
    editChannel(channel) {
      this.dispatch('CHANGE_CHANNEL', { channel });
    },
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.resetFields();
      }
    },
    updateField(field, value) {
      this.dispatch('UPDATE_FIELD', { [field]: value });
    },
    updateFormFlags(flags) {
      this.dispatch('UPDATE_CHANNEL_FORM_FLAGS', flags);
    },
    updateFormErrors(errors) {
      this.dispatch('UPDATE_CHANNEL_FORM_ERRORS', errors);
    },
    updateFormFieldsFlags(fields) {
      this.dispatch('UPDATE_CHANNEL_FORM_FIELDS', fields);
    },
    validateForm() {
      if (this.$refs.form) {
        this.$refs.form.validateAndShowErrors();
      }
    },
    validateFormForPublish() {
      if (this.$refs.form) {
        this.$refs.form.validateForPublishAndShowErrors();
      }
    },
    isDirty() {
      return this.state.isDirty;
    },
    async save() {
      await this.dispatch('SAVE');
      this.onSave();
    },
    onSave() {
      this.$refs.form.resetFields();
      this.reloadPreview();
    },
    reloadPreview(queryParams) {
      this.$refs.preview.reload(queryParams);
    },
    updateRelativeVersionId(relativeVersionId) {
      this.$emit('updateRelativeVersionId', relativeVersionId);
    },
    modifyAvailableLanguages(availableLanguages) {
      this.dispatch('MODIFY_AVAILABLE_LANGUAGES', availableLanguages);
    },
  },
};
</script>

<style lang="stylus">
.nx-content-editor {
  .nx-container {
    margin-top: 64px;
  }

  &.modal {
    .nx-container {
      margin-top: 112px;
    }
  }

  &.is-preview {
    .nx-entity-form-generator {
      .nx-form-generator-tabs {
        display: none;
      }

      @media (min-width: 1264px) {
        .nx-form-generator-form {
          &.flex.offset-lg3 {
            margin-left: 6%;
            flex-basis: 93%;
            max-width: 93%;
          }
        }
      }
    }
  }
}
</style>
